import { useRef, useCallback } from 'react'
import { toast, ToastOptions, Id } from 'react-toastify'

type InteractiveToastOptions = ToastOptions & {
  containerId?: string
}

type InteractiveToastReturn = {
  open: (content: React.ReactNode, options?: InteractiveToastOptions) => void
  update: (options: ToastOptions) => void
  dismiss: () => void
}

type GeneralToastReturn = {
  success: (msg: string) => void
  error: (msg: string) => void
  info: (msg: string) => void
  warning: (msg: string) => void
}

export const useInteractiveToast = (): InteractiveToastReturn => {
  const toastId = useRef<Id | null>(null)

  const open = useCallback((content: React.ReactNode, options?: InteractiveToastOptions) => {
    if (toastId.current && toast.isActive(toastId.current)) {
      toast.dismiss(toastId.current)
    }
    toastId.current = toast(content, {
      containerId: 'interactive-toast-container',
      ...options,
    })
  }, [])

  const update = useCallback((options: ToastOptions) => {
    if (toastId.current && toast.isActive(toastId.current)) {
      toast.update(toastId.current, options)
    }
  }, [])

  const dismiss = useCallback(() => {
    if (toastId.current) {
      toast.dismiss(toastId.current)
    }
  }, [])

  return { open, update, dismiss }
}

export const useGeneralToast = (): GeneralToastReturn => {
  const success = useCallback((msg: string) => {
    toast.success(msg, { containerId: 'general-toast-container' })
  }, [])

  const error = useCallback((msg: string) => {
    toast.error(msg, { containerId: 'general-toast-container' })
  }, [])

  const info = useCallback((msg: string) => {
    toast.info(msg, { containerId: 'general-toast-container' })
  }, [])

  const warning = useCallback((msg: string) => {
    toast.warning(msg, { containerId: 'general-toast-container' })
  }, [])

  return {
    success,
    error,
    info,
    warning,
  }
}
